// Versão: 3.02
// Data..: 2026-09-26
// v3.02: página em .full-bleed (ocupa toda a largura do painel, sem limite de 1600px).
// Painel com métricas: cartões de status, gráficos por prioridade/departamento e recentes.
// v3.01: KPIs compactos (stat-card), layout mais denso e profissional, sem botão duplicado.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TicketStatus, type Ticket, type Department, type Paginated } from '@makweb/shared';
import { api } from '../lib/api.js';
import { StatusBadge, PriorityBadge } from '../components/Badge.js';
import { BarChart } from '../components/BarChart.js';
import { PageHeader } from '../components/PageHeader.js';

interface Stats {
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  byDepartment: Record<string, number>;
  recent: Ticket[];
  total: number;
}

const statusCards = [
  { status: TicketStatus.OPEN, label: 'Abertos', icon: '📥', color: 'var(--info)' },
  { status: TicketStatus.IN_PROGRESS, label: 'Em atendimento', icon: '⚙', color: 'var(--brand)' },
  { status: TicketStatus.WAITING_CLIENT, label: 'Aguardando', icon: '⏳', color: 'var(--warning)' },
  { status: TicketStatus.SCHEDULED, label: 'Agendados', icon: '📅', color: 'var(--purple)' },
];

const priorityLabels: Record<string, string> = { low: 'Baixa', normal: 'Normal', high: 'Alta', urgent: 'Urgente' };
const priorityColors: Record<string, string> = {
  low: 'var(--text-3)', normal: 'var(--info)', high: 'var(--warning)', urgent: 'var(--danger)',
};

export function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [depts, setDepts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<Stats>('/stats'),
      api.get<Paginated<Department>>('/departments').catch(() => ({ items: [] as Department[] })),
    ])
      .then(([s, d]) => {
        setStats(s);
        const map: Record<string, string> = {};
        d.items.forEach((x) => (map[x.id] = x.name));
        setDepts(map);
      })
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>;

  const priorityData = ['urgent', 'high', 'normal', 'low'].map((p) => ({
    label: priorityLabels[p], value: stats?.byPriority[p] ?? 0, color: priorityColors[p],
  }));
  const deptData = Object.entries(stats?.byDepartment ?? {}).map(([id, value]) => ({
    label: id === 'sem' ? 'Sem departamento' : depts[id] ?? 'Outro', value,
  }));

  const totalOpen = statusCards.reduce((sum, c) => sum + (stats?.byStatus[c.status] ?? 0), 0);
  const total = stats?.total ?? 0;
  const urgentCount = stats?.byPriority['urgent'] ?? 0;

  return (
    <div className="full-bleed">
      <PageHeader
        eyebrow="Visão geral"
        title="Painel"
        description="Indicadores de atendimento em tempo real."
      />

      {/* Faixa de destaque */}
      <div className="dash-hero mb-4">
        <div className="dash-hero-main">
          <span className="dash-hero-eyebrow">Chamados em aberto</span>
          <div className="dash-hero-value">{totalOpen}</div>
          <div className="dash-hero-sub">
            {total > 0 ? `${total} chamados no total` : 'Nenhum chamado registrado ainda'}
            {urgentCount > 0 && <span className="dash-hero-flag">🔴 {urgentCount} urgente(s)</span>}
          </div>
        </div>
        <Link to="/tickets/new" className="btn btn-primary btn-lg dash-hero-cta">+ Abrir chamado</Link>
      </div>

      {/* KPIs compactos */}
      <div className="kpi-grid">
        {statusCards.map((c) => (
          <Link key={c.status} to={`/tickets?status=${c.status}`} className="kpi" style={{ ['--kpi-accent' as string]: c.color }}>
            <span className="kpi-icon">{c.icon}</span>
            <div className="kpi-body">
              <div className="kpi-value">{stats?.byStatus[c.status] ?? 0}</div>
              <div className="kpi-label">{c.label}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Gráficos */}
      <div className="dash-charts mt-4">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="card-title"><span className="card-head-icon">📊</span><h3 className="mb-0">Por prioridade</h3></div>
            <span className="text-dim text-xs">{totalOpen} em aberto</span>
          </div>
          <BarChart data={priorityData} />
        </div>
        <div className="card">
          <div className="card-title mb-4"><span className="card-head-icon">🗂</span><h3 className="mb-0">Por departamento</h3></div>
          <BarChart data={deptData} emptyText="Nenhum chamado com departamento definido." />
        </div>
      </div>

      {/* Recentes */}
      <div className="card card-flush mt-4">
        <div className="flex items-center justify-between" style={{ padding: 'var(--s3) var(--s5)', borderBottom: '1px solid var(--border)' }}>
          <h3 className="mb-0">Chamados recentes</h3>
          <Link to="/tickets" className="text-sm">Ver todos →</Link>
        </div>
        {(stats?.recent.length ?? 0) === 0 ? (
          <div className="empty"><div className="empty-icon">🎫</div>Nenhum chamado ainda.</div>
        ) : (
          <table className="table">
            <thead><tr><th>#</th><th>Assunto</th><th>Status</th><th className="hide-mobile">Prioridade</th><th className="hide-mobile">Atualizado</th></tr></thead>
            <tbody>
              {stats?.recent.map((t) => (
                <tr key={t.id}>
                  <td className="text-dim">#{t.number}</td>
                  <td><Link to={`/tickets/${t.id}`} className="font-semibold" style={{ color: 'var(--text)' }}>{t.subject}</Link></td>
                  <td><StatusBadge status={t.status} /></td>
                  <td className="hide-mobile"><PriorityBadge priority={t.priority} /></td>
                  <td className="hide-mobile text-dim text-sm">{new Date(t.updatedAt).toLocaleString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
